// #region > Protect Testing - STATIC

/**
 * Singleton instance for SWAP Protect's internal test runner. Tests are organised into named
 * groups (e.g. 'Code: SWAP.Protect.Product') and registered with SWAP.Protect.Testing.register.
 * @namespace SWAP.Protect.Testing
 * @memberof SWAP.Protect
 */
SWAP.Protect.Testing = {};

SWAP.Protect.Testing.groups = {};

/**
 * Throws an Error with the given message if condition is falsy. Used inside registered tests
 * to fail with a specific reason.
 * @memberof SWAP.Protect.Testing
 * @param {boolean} condition The condition which must be true for the test to pass.
 * @param {string} message The message to fail with if condition is falsy.
 * @example
 * SWAP.Protect.Testing.assert(1 + 1 === 2, '1 + 1 should equal 2.');
 */
SWAP.Protect.Testing.assert = function(condition, message) {
    if(!condition) {
        throw new Error(message || 'Assertion failed.');
    }
}

/**
 * Registers a test function under a named group, creating the group if it doesn't exist yet.
 * @memberof SWAP.Protect.Testing
 * @param {string} group The name of the group to register under, e.g. 'Code: SWAP.Protect.Product'.
 * @param {string} name A short description of what the test checks.
 * @param {function} test A function (sync or async) which throws - e.g. via SWAP.Protect.Testing.assert - to fail.
 * @example
 * SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'does a thing', function() {
 *     SWAP.Protect.Testing.assert(true, 'should always pass');
 * });
 */
SWAP.Protect.Testing.register = function(group, name, test) {
    if(SWAP.Protect.Testing.groups[group] === undefined) {
        SWAP.Protect.Testing.groups[group] = {};
    }

    SWAP.Protect.Testing.groups[group][name] = test;
}

/**
 * Temporarily overrides SWAP.Protect.Product.Protect and/or SWAP.Protect.settings for the
 * duration of callback, restoring the originals afterwards - even if callback throws. Lets
 * tests exercise functions which read from those globals with deterministic fixture data,
 * without depending on, or permanently mutating, the widget's real live state.
 * @memberof SWAP.Protect.Testing
 * @param {object} overrides An object which may contain a `protect` and/or `settings` key.
 * @param {function} callback The function to run while the overrides are applied.
 * @returns {Promise<*>} Whatever callback returns/resolves to.
 * @example
 * await SWAP.Protect.Testing.withFixture({ settings: { restrictions: { limit: 500 } } }, function() {
 *     SWAP.Protect.Testing.assert(SWAP.Protect.Product.protectCalculateLimit() !== undefined);
 * });
 */
SWAP.Protect.Testing.withFixture = async function(overrides, callback) {
    let originalProtect = SWAP.Protect.Product.Protect;
    let originalSettings = SWAP.Protect.settings;

    try {
        if(overrides.protect !== undefined) {
            SWAP.Protect.Product.Protect = overrides.protect;
        }

        if(overrides.settings !== undefined) {
            SWAP.Protect.settings = overrides.settings;
        }

        return await callback();
    } finally {
        SWAP.Protect.Product.Protect = originalProtect;
        SWAP.Protect.settings = originalSettings;
    }
}

/**
 * Temporarily replaces named functions on SWAP.Protect.Product for the duration of callback,
 * restoring the originals afterwards - even if callback throws. Lets a test isolate a
 * function's own logic (e.g. protectCheck's branching) from what its collaborators (e.g.
 * protectReview, protectRemove, protectAdd) actually do.
 * @memberof SWAP.Protect.Testing
 * @param {object} stubs An object mapping SWAP.Protect.Product function names to replacements.
 * @param {function} callback The function to run while the stubs are applied.
 * @returns {Promise<*>} Whatever callback returns/resolves to.
 * @example
 * await SWAP.Protect.Testing.withStubs({ protectReview: async () => SWAP.Protect.Product.ReviewAction.NONE }, async function() {
 *     SWAP.Protect.Testing.assert(await SWAP.Protect.Product.protectCheck({ items: [] }) === true);
 * });
 */
SWAP.Protect.Testing.withStubs = async function(stubs, callback) {
    let product = SWAP.Protect.Product;
    let originals = {};

    Object.keys(stubs).forEach(function(name) {
        originals[name] = product[name];
        product[name] = stubs[name];
    });

    try {
        return await callback();
    } finally {
        Object.keys(stubs).forEach(function(name) {
            product[name] = originals[name];
        });
    }
}

/**
 * Attempts to find the Shopify product currently being viewed on the page, so live tests can
 * exercise SWAP Protect against a real, purchasable variant instead of a fabricated one. Checks
 * window.ShopifyAnalytics.meta.product first (present on most Shopify product pages via the
 * theme's built-in analytics snippet), then falls back to fetching the product directly if the
 * URL looks like a product page.
 * @memberof SWAP.Protect.Testing
 * @returns {Promise<object|undefined>} The current page's product, or undefined if it could not be determined.
 */
SWAP.Protect.Testing.getCurrentPageProduct = async function() {
    if(window.ShopifyAnalytics?.meta?.product !== undefined) {
        return window.ShopifyAnalytics.meta.product;
    }

    let match = window.location.pathname.match(/\/products\/([^/?#]+)/);

    if(match === null) {
        return undefined;
    }

    try {
        let response = await fetch(window.Shopify.routes.root + 'products/' + match[1] + '.js');
        return await response.json();
    } catch(error) {
        SWAP.log('error', "Testing getCurrentPageProduct error.", error);
        return undefined;
    }
}

/**
 * Snapshots the live cart's current quantity for each of the given variant ids, runs callback,
 * then restores those exact quantities afterwards - even if callback throws. Lets live tests
 * add/remove real cart line items (e.g. via protectAdd, protectCheck, or cart/add.js directly)
 * without permanently altering whatever was in the cart before the test ran.
 * @memberof SWAP.Protect.Testing
 * @param {Array<number>} variantIds The variant ids whose cart quantity should be restored afterwards.
 * @param {function} callback The function to run - receives the cart as it was before any changes.
 * @returns {Promise<*>} Whatever callback returns/resolves to.
 */
SWAP.Protect.Testing.withRealCart = async function(variantIds, callback) {
    let product = SWAP.Protect.Product;
    let originalCart = await product.cartGet();

    if(originalCart === undefined) {
        throw new Error('Could not fetch the live cart.');
    }

    let originalQuantities = {};

    variantIds.forEach(function(id) {
        let lineItem = originalCart.items.find(function(item) { return item.id === id; });
        originalQuantities[id] = (lineItem !== undefined) ? lineItem.quantity : 0;
    });

    try {
        return await callback(originalCart);
    } finally {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: originalQuantities })
        });
    }
}

/**
 * Empties the live cart, runs callback, then fully restores the cart to its exact original
 * contents afterwards - even if callback throws, and even if callback added items of its own
 * that withRealCart wouldn't know to clean up. Used for scenario tests which need a cart
 * containing only specific, deliberately-added items.
 * @memberof SWAP.Protect.Testing
 * @param {function} callback The function to run against an emptied cart.
 * @returns {Promise<*>} Whatever callback returns/resolves to.
 */
SWAP.Protect.Testing.withEmptyCart = async function(callback) {
    let product = SWAP.Protect.Product;
    let originalCart = await product.cartGet();

    if(originalCart === undefined) {
        throw new Error('Could not fetch the live cart.');
    }

    async function setQuantities(updates) {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
        });
    }

    let clearUpdates = {};
    originalCart.items.forEach(function(item) { clearUpdates[item.id] = 0; });

    try {
        if(Object.keys(clearUpdates).length > 0) {
            await setQuantities(clearUpdates);
        }

        return await callback();
    } finally {
        let finalCart = await product.cartGet();
        let restoreUpdates = {};

        if(finalCart !== undefined) {
            finalCart.items.forEach(function(item) { restoreUpdates[item.id] = 0; });    // Clear out anything the test added...
        }

        originalCart.items.forEach(function(item) { restoreUpdates[item.id] = item.quantity; });   // ...then put the originals back.

        await setQuantities(restoreUpdates);
    }
}

/**
 * Temporarily overrides specific keys on the real SWAP.Protect.settings.restrictions object
 * for the duration of callback, restoring the originals afterwards - even if callback throws.
 * Unlike withFixture (which swaps the whole settings object), this only touches the given
 * keys, so the rest of the live settings (multiplier, limit, ignore, etc.) stay real.
 * @memberof SWAP.Protect.Testing
 * @param {object} overrides Keys to temporarily set on SWAP.Protect.settings.restrictions.
 * @param {function} callback The function to run while the overrides are applied.
 * @returns {Promise<*>} Whatever callback returns/resolves to.
 */
SWAP.Protect.Testing.withRestrictionsOverride = async function(overrides, callback) {
    let restrictions = SWAP.Protect.settings.restrictions;
    let originalValues = {};

    Object.keys(overrides).forEach(function(key) {
        originalValues[key] = restrictions[key];
        restrictions[key] = overrides[key];
    });

    try {
        return await callback();
    } finally {
        Object.keys(overrides).forEach(function(key) {
            restrictions[key] = originalValues[key];
        });
    }
}

/**
 * Adds a variant to the live cart via Shopify's cart/add.js AJAX API.
 * @memberof SWAP.Protect.Testing
 * @param {number} id The variant id to add.
 * @param {number} [quantity=1] The quantity to add.
 */
SWAP.Protect.Testing.addVariant = async function(id, quantity) {
    await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: quantity || 1 }] })
    });
}

/**
 * Sorts the live Protect variants into brackets by their converted limit, and figures out
 * which one protectCalculateFee considers correct for cart, plus its immediate neighbours -
 * a cheaper bracket that would undercharge, and a pricier one that would overcharge.
 * @memberof SWAP.Protect.Testing
 * @param {object} cart The Shopify cart object, as returned by cart.js.
 * @returns {object} { fee, correct, under, over } - correct/under/over may be undefined if
 * there's no bracket in that position (e.g. the correct bracket is already the cheapest).
 */
SWAP.Protect.Testing.getProtectBrackets = function(cart) {
    let rate = parseFloat(window.Shopify.currency.rate) || 1;
    let calculateValueFromTitle = SWAP.Protect.Product.calculateValueFromTitle;

    let sorted = SWAP.Protect.Product.Protect.variants
        .map(function(variant) {
            return { variant: variant, limit: calculateValueFromTitle(variant.title) * rate };
        })
        .sort(function(a, b) { return a.limit - b.limit; });

    let fee = SWAP.Protect.Product.protectCalculateFee(cart);
    let correctIndex = sorted.findIndex(function(entry) { return entry.variant.id === fee.id; });

    return {
        fee: fee,
        correct: sorted[correctIndex]?.variant,
        under: sorted[correctIndex - 1]?.variant,    // A cheaper bracket than required - undercharges.
        over: sorted[correctIndex + 1]?.variant      // A pricier bracket than required - overcharges.
    };
}

/**
 * Runs every test registered under a group and returns a summary of the results.
 * @memberof SWAP.Protect.Testing
 * @param {string} group The name of the group to run, e.g. 'Code: SWAP.Protect.Product'.
 * @returns {Promise<object>} { group, total, passed, tests: [{ name, passed, error }] }
 * @example
 * let results = await SWAP.Protect.Testing.run('Code: SWAP.Protect.Product');
 */
SWAP.Protect.Testing.run = async function(group) {
    let tests = SWAP.Protect.Testing.groups[group];

    let results = {
        group: group,
        total: 0,
        passed: 0,
        tests: []
    };

    if(tests === undefined) {
        SWAP.log('error', "Testing run error. Group not found.", group);
        return results;
    }

    for(let name in tests) {
        results.total++;

        try {
            await tests[name]();
            results.passed++;
            results.tests.push({ name: name, passed: true });
        } catch(error) {
            results.tests.push({ name: name, passed: false, error: error.message });
            SWAP.log('warning', "Test failed.", group, name, error);
        }
    }

    return results;
}

/**
 * Runs every registered group and returns a summary of the combined results.
 * @memberof SWAP.Protect.Testing
 * @returns {Promise<object>} { total, passed, groups: [ ...one entry per group, shaped like SWAP.Protect.Testing.run's result... ] }
 * @example
 * let results = await SWAP.Protect.Testing.runAll();
 */
SWAP.Protect.Testing.runAll = async function() {
    let groupResults = [];

    for(let group in SWAP.Protect.Testing.groups) {
        groupResults.push(await SWAP.Protect.Testing.run(group));
    }

    return {
        total: groupResults.reduce(function(sum, result) { return sum + result.total; }, 0),
        passed: groupResults.reduce(function(sum, result) { return sum + result.passed; }, 0),
        groups: groupResults
    };
}

// #region >> Group: Code: SWAP.Protect.Product

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'calculateValueFromTitle applies the configured multiplier', async function() {
    let assert = SWAP.Protect.Testing.assert;

    await SWAP.Protect.Testing.withFixture({ settings: { display: { product: { textnames: { multiplier: 100 } } } } }, function() {
        let value = SWAP.Protect.Product.calculateValueFromTitle('£25');
        assert(value === 2500, 'Expected "£25" with a x100 multiplier to equal 2500, got ' + value + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'calculateValueFromTitle parses plainly without a multiplier', async function() {
    let assert = SWAP.Protect.Testing.assert;

    await SWAP.Protect.Testing.withFixture({ settings: { display: { product: { textnames: {} } } } }, function() {
        let value = SWAP.Protect.Product.calculateValueFromTitle('25');
        assert(value === 25, 'Expected "25" with no multiplier to equal 25, got ' + value + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCalculateLimit converts the configured limit using the currency rate', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let rate = parseFloat(window.Shopify.currency.rate) || 1;

    await SWAP.Protect.Testing.withFixture({ settings: { restrictions: { limit: 500 } } }, function() {
        let value = SWAP.Protect.Product.protectCalculateLimit();
        assert(value === 500 * rate, 'Expected the limit to be converted by the currency rate, got ' + value + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCalculateLimit returns undefined when no limit is configured', async function() {
    let assert = SWAP.Protect.Testing.assert;

    await SWAP.Protect.Testing.withFixture({ settings: { restrictions: {} } }, function() {
        let value = SWAP.Protect.Product.protectCalculateLimit();
        assert(value === undefined, 'Expected no limit to be configured, got ' + value + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCalculateFee selects the cheapest bracket that covers the cart', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let rate = parseFloat(window.Shopify.currency.rate) || 1;

    let fixture = {
        protect: {
            variants: [
                { id: 9001, title: '£25', price: 100 },
                { id: 9002, title: '£50', price: 200 },
                { id: 9003, title: '£100', price: 300 }
            ]
        },
        settings: {
            display: { product: { textnames: { multiplier: 100 } } },
            restrictions: { ignore: [] }
        }
    };

    let cart = {
        total_price: 3000 * rate,
        items: [
            { id: 1, product_id: 1, price: 3000 * rate, quantity: 1 }
        ]
    };

    await SWAP.Protect.Testing.withFixture(fixture, function() {
        let result = SWAP.Protect.Product.protectCalculateFee(cart);

        assert(result !== undefined, 'Expected a result object.');
        assert(result.cartTotal === cart.total_price, 'Expected cartTotal to match the cart\'s total_price.');
        assert(result.feeTotalCurrent === 0, 'Expected no fee to currently be applied.');
        assert(result.id === 9002, 'Expected the £50 bracket to be selected, got variant ' + result.id + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCalculateFee counts an existing Protect line item towards feeTotalCurrent', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let rate = parseFloat(window.Shopify.currency.rate) || 1;

    let fixture = {
        protect: {
            variants: [
                { id: 9001, title: '£25', price: 100 },
                { id: 9002, title: '£50', price: 200 }
            ]
        },
        settings: {
            display: { product: { textnames: { multiplier: 100 } } },
            restrictions: { ignore: [] }
        }
    };

    let cart = {
        total_price: 3200 * rate,
        items: [
            { id: 1, product_id: 1, price: 3000 * rate, quantity: 1 },
            { id: 9002, product_id: 2, price: 200, quantity: 1 }
        ]
    };

    await SWAP.Protect.Testing.withFixture(fixture, function() {
        let result = SWAP.Protect.Product.protectCalculateFee(cart);

        assert(result.feeTotalCurrent === 200, 'Expected the existing Protect fee to be counted, got ' + result.feeTotalCurrent + '.');
        assert(result.feeCount === 1, 'Expected exactly one Protect line item to be counted, got ' + result.feeCount + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCalculateFee excludes ignored/restricted products from the protectable total', async function() {
    let assert = SWAP.Protect.Testing.assert;

    let fixture = {
        protect: {
            variants: [
                { id: 9001, title: '£25', price: 100 }
            ]
        },
        settings: {
            display: { product: { textnames: { multiplier: 100 } } },
            restrictions: { ignore: [4242] }
        }
    };

    let cart = {
        total_price: 2000,
        items: [
            { id: 4242, product_id: 4242, price: 500, quantity: 1 },
            { id: 2, product_id: 2, price: 1500, quantity: 1 }
        ]
    };

    await SWAP.Protect.Testing.withFixture(fixture, function() {
        let result = SWAP.Protect.Product.protectCalculateFee(cart);
        assert(result.protectableTotal === 1500, 'Expected the ignored product\'s value to be excluded, got ' + result.protectableTotal + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectReview suggests REMOVE when a restricted product is in the cart', async function() {
    let assert = SWAP.Protect.Testing.assert;

    let fixture = {
        protect: { variants: [{ id: 9001, title: '£25', price: 100 }] },
        settings: {
            display: { product: { textnames: { multiplier: 100 } } },
            restrictions: { products: [5555], ignore: [] }
        }
    };

    let cart = {
        total_price: 1000,
        items: [{ id: 1, product_id: 5555, price: 1000, quantity: 1 }]
    };

    await SWAP.Protect.Testing.withFixture(fixture, async function() {
        let action = await SWAP.Protect.Product.protectReview(cart);
        assert(action === SWAP.Protect.Product.ReviewAction.REMOVE, 'Expected REMOVE, got ' + action + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectReview suggests REMOVE when Protect is in the cart with nothing to protect', async function() {
    let assert = SWAP.Protect.Testing.assert;

    let fixture = {
        protect: { variants: [{ id: 9001, title: '£25', price: 100 }] },
        settings: {
            display: { product: { textnames: { multiplier: 100 } } },
            restrictions: { products: [], ignore: [] }
        }
    };

    let cart = {
        total_price: 100,
        items: [{ id: 9001, product_id: 9001, price: 100, quantity: 1 }]
    };

    await SWAP.Protect.Testing.withFixture(fixture, async function() {
        let action = await SWAP.Protect.Product.protectReview(cart);
        assert(action === SWAP.Protect.Product.ReviewAction.REMOVE, 'Expected REMOVE, got ' + action + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectReview suggests REMOVE when the cart is over the configured limit', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let rate = parseFloat(window.Shopify.currency.rate) || 1;

    let fixture = {
        protect: { variants: [{ id: 9001, title: '£25', price: 100 }] },
        settings: {
            display: { product: { textnames: { multiplier: 100 } } },
            restrictions: { limit: 1000, products: [], ignore: [] }
        }
    };

    let cart = {
        total_price: 200000 * rate,
        items: [{ id: 1, product_id: 1, price: 200000 * rate, quantity: 1 }]
    };

    await SWAP.Protect.Testing.withFixture(fixture, async function() {
        let action = await SWAP.Protect.Product.protectReview(cart);
        assert(action === SWAP.Protect.Product.ReviewAction.REMOVE, 'Expected REMOVE, got ' + action + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectReview suggests UPDATE when the applied fee does not match', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let rate = parseFloat(window.Shopify.currency.rate) || 1;

    let fixture = {
        protect: {
            variants: [
                { id: 9001, title: '£25', price: 100 },
                { id: 9002, title: '£50', price: 200 }
            ]
        },
        settings: {
            display: { product: { textnames: { multiplier: 100 } } },
            restrictions: { products: [], ignore: [] }
        }
    };

    let cart = {
        total_price: 3200 * rate,
        items: [
            { id: 1, product_id: 1, price: 3000 * rate, quantity: 1 },
            { id: 9001, product_id: 9001, price: 100, quantity: 1 }   // Wrong bracket applied - should be 9002.
        ]
    };

    await SWAP.Protect.Testing.withFixture(fixture, async function() {
        let action = await SWAP.Protect.Product.protectReview(cart);
        assert(action === SWAP.Protect.Product.ReviewAction.UPDATE, 'Expected UPDATE, got ' + action + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectReview suggests NONE when the correct fee is already applied', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let rate = parseFloat(window.Shopify.currency.rate) || 1;

    let fixture = {
        protect: {
            variants: [
                { id: 9001, title: '£25', price: 100 },
                { id: 9002, title: '£50', price: 200 }
            ]
        },
        settings: {
            display: { product: { textnames: { multiplier: 100 } } },
            restrictions: { products: [], ignore: [] }
        }
    };

    let cart = {
        total_price: 3200 * rate,
        items: [
            { id: 1, product_id: 1, price: 3000 * rate, quantity: 1 },
            { id: 9002, product_id: 9002, price: 200, quantity: 1 }   // Correct bracket already applied.
        ]
    };

    await SWAP.Protect.Testing.withFixture(fixture, async function() {
        let action = await SWAP.Protect.Product.protectReview(cart);
        assert(action === SWAP.Protect.Product.ReviewAction.NONE, 'Expected NONE, got ' + action + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'cartGet, fetch, protectAdd, protectRemove and protectCheck are defined', function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;

    assert(typeof product.cartGet === 'function', 'Expected cartGet to be a function.');
    assert(typeof product.fetch === 'function', 'Expected fetch to be a function.');
    assert(typeof product.protectAdd === 'function', 'Expected protectAdd to be a function.');
    assert(typeof product.protectRemove === 'function', 'Expected protectRemove to be a function.');
    assert(typeof product.protectCheck === 'function', 'Expected protectCheck to be a function.');
});

// The following isolate protectCheck's own branching logic from its collaborators
// (protectReview, protectRemove, protectAdd, protectCalculateFee, cartGet), using
// withStubs - the counterpart to the protectReview tests above, but one level up the chain.

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCheck does nothing when protectReview suggests NONE', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;
    let removeCalled = false;
    let addCalled = false;

    await SWAP.Protect.Testing.withStubs({
        protectReview: async function() { return product.ReviewAction.NONE; },
        protectRemove: async function() { removeCalled = true; return true; },
        protectAdd: async function() { addCalled = true; return true; }
    }, async function() {
        let result = await product.protectCheck({ items: [] });

        assert(result === true, 'Expected protectCheck to return true.');
        assert(removeCalled === false, 'Expected protectRemove not to be called.');
        assert(addCalled === false, 'Expected protectAdd not to be called.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCheck calls protectRemove when protectReview suggests REMOVE', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;
    let removeCalledWith;
    let addCalled = false;

    await SWAP.Protect.Testing.withStubs({
        protectReview: async function() { return product.ReviewAction.REMOVE; },
        protectRemove: async function(cart) { removeCalledWith = cart; return true; },
        protectAdd: async function() { addCalled = true; return true; }
    }, async function() {
        let cart = { items: [], marker: 'test-cart' };
        let result = await product.protectCheck(cart);

        assert(result === true, 'Expected protectCheck to return true.');
        assert(removeCalledWith === cart, 'Expected protectRemove to be called with the same cart.');
        assert(addCalled === false, 'Expected protectAdd not to be called.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCheck returns false when protectRemove fails during REMOVE', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;

    await SWAP.Protect.Testing.withStubs({
        protectReview: async function() { return product.ReviewAction.REMOVE; },
        protectRemove: async function() { return false; }
    }, async function() {
        let result = await product.protectCheck({ items: [] });
        assert(result === false, 'Expected protectCheck to return false when protectRemove fails.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCheck removes then adds the recalculated variant when protectReview suggests UPDATE', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;
    let callOrder = [];

    await SWAP.Protect.Testing.withStubs({
        protectReview: async function() { return product.ReviewAction.UPDATE; },
        protectRemove: async function() { callOrder.push('remove'); return true; },
        protectCalculateFee: function() { return { id: 12345 }; },
        protectAdd: async function(id) { callOrder.push('add:' + id); return true; }
    }, async function() {
        let result = await product.protectCheck({ items: [] });

        assert(result === true, 'Expected protectCheck to return true.');
        assert(callOrder[0] === 'remove', 'Expected protectRemove to be called first, got ' + callOrder[0] + '.');
        assert(callOrder[1] === 'add:12345', 'Expected protectAdd to be called with the recalculated variant id, got ' + callOrder[1] + '.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCheck skips protectAdd when protectRemove fails during UPDATE', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;
    let addCalled = false;

    await SWAP.Protect.Testing.withStubs({
        protectReview: async function() { return product.ReviewAction.UPDATE; },
        protectRemove: async function() { return false; },
        protectAdd: async function() { addCalled = true; return true; }
    }, async function() {
        let result = await product.protectCheck({ items: [] });

        assert(result === false, 'Expected protectCheck to return false.');
        assert(addCalled === false, 'Expected protectAdd not to be called when protectRemove fails.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCheck fetches the cart itself when none is passed', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;
    let fetchedCart = { items: [], marker: 'fetched' };
    let reviewCalledWith;

    await SWAP.Protect.Testing.withStubs({
        cartGet: async function() { return fetchedCart; },
        protectReview: async function(cart) { reviewCalledWith = cart; return product.ReviewAction.NONE; }
    }, async function() {
        let result = await product.protectCheck();

        assert(result === true, 'Expected protectCheck to return true.');
        assert(reviewCalledWith === fetchedCart, "Expected protectCheck to pass the fetched cart to protectReview.");
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCheck returns false when the cart cannot be fetched', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;

    await SWAP.Protect.Testing.withStubs({
        cartGet: async function() { return undefined; }
    }, async function() {
        let result = await product.protectCheck();
        assert(result === false, 'Expected protectCheck to return false when the cart could not be fetched.');
    });
});

SWAP.Protect.Testing.register('Code: SWAP.Protect.Product', 'protectCheck returns false when protectReview throws', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;

    await SWAP.Protect.Testing.withStubs({
        protectReview: async function() { throw new Error('boom'); }
    }, async function() {
        let result = await product.protectCheck({ items: [] });
        assert(result === false, 'Expected protectCheck to return false when protectReview throws.');
    });
});

// #endregion >> Group: Code: SWAP.Protect.Product

// #region >> Group: Live: SWAP.Protect.Product
//
// Unlike the "Code" group above, these tests do NOT use withFixture - they run against
// whatever SWAP.Protect.settings and SWAP.Protect.Product.Protect are actually loaded for the
// brand this script is currently installed on, to sanity check the real, live configuration.
// A couple of these tests also read and write the real Shopify cart (wrapped in
// SWAP.Protect.Testing.withRealCart, which restores whatever was there beforehand) - only run
// this group manually, in a browser console, on a store you're happy to briefly touch the
// cart of. The final test needs a real product to add, so it must be run from a product page;
// it uses SWAP.Protect.Testing.getCurrentPageProduct to pick up whatever product the page is
// currently displaying, rather than a hardcoded/fabricated variant id.

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'the configured Protect product slug exists in the store', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let slug = SWAP.Protect.settings?.display?.product?.slug;

    assert(slug !== undefined, 'No Protect product slug is configured in settings.display.product.slug.');

    let response = await fetch(window.Shopify.routes.root + 'products/' + slug + '.js');

    assert(response.ok, 'Expected the store to have a product at /products/' + slug + '.js, got status ' + response.status + '.');
});

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'the live SWAP Protect product has at least one variant', function() {
    let assert = SWAP.Protect.Testing.assert;
    let variants = SWAP.Protect.Product.Protect?.variants;

    assert(Array.isArray(variants) && variants.length > 0, 'Expected the live Protect product to have at least one variant.');
});

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'calculateValueFromTitle parses every live variant title to a positive number', function() {
    let assert = SWAP.Protect.Testing.assert;
    let variants = SWAP.Protect.Product.Protect.variants;

    variants.forEach(function(variant) {
        let value = SWAP.Protect.Product.calculateValueFromTitle(variant.title);
        assert(Number.isFinite(value) && value > 0, 'Expected variant "' + variant.title + '" to parse to a positive number, got ' + value + '.');
    });
});

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'protectCalculateLimit returns a sensible value for the live settings', function() {
    let assert = SWAP.Protect.Testing.assert;
    let limit = SWAP.Protect.Product.protectCalculateLimit();

    assert(limit === undefined || (Number.isFinite(limit) && limit > 0), 'Expected the live limit to be undefined or a positive number, got ' + limit + '.');
});

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'protectCalculateFee returns a well-formed result for the live cart', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let cart = await SWAP.Protect.Product.cartGet();

    assert(cart !== undefined, 'Could not fetch the live cart.');

    let result = SWAP.Protect.Product.protectCalculateFee(cart);

    assert(result !== undefined, 'Expected protectCalculateFee to return a result.');
    assert(typeof result.cartTotal === 'number', 'Expected cartTotal to be a number, got ' + typeof result.cartTotal + '.');
    assert(typeof result.feeTotalCurrent === 'number', 'Expected feeTotalCurrent to be a number, got ' + typeof result.feeTotalCurrent + '.');
});

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'protectReview returns a valid ReviewAction for the live cart', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let cart = await SWAP.Protect.Product.cartGet();

    assert(cart !== undefined, 'Could not fetch the live cart.');

    let action = await SWAP.Protect.Product.protectReview(cart);
    let validActions = Object.values(SWAP.Protect.Product.ReviewAction);

    assert(validActions.includes(action), 'Expected a valid ReviewAction, got ' + action + '.');
});

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'protectCheck reconciles the live cart without throwing', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let testing = SWAP.Protect.Testing;
    let product = SWAP.Protect.Product;

    let cart = await product.cartGet();
    assert(cart !== undefined, 'Could not fetch the live cart.');

    let protectVariantIds = product.Protect.variants.map(function(variant) { return variant.id; });
    let variantIdsToRestore = [...protectVariantIds];
    let pageVariant;

    if(cart.items.length === 0) {   // A fresh cart has nothing to reconcile - add the current page's product so protectCheck has something to work with.
        let pageProduct = await testing.getCurrentPageProduct();
        assert(pageProduct !== undefined, 'The cart is empty and no product could be found on the current page - run this test from a product page.');

        pageVariant = pageProduct.variants.find(function(v) { return v.available; }) || pageProduct.variants[0];
        assert(pageVariant !== undefined, "The current page's product has no variants.");

        variantIdsToRestore.push(pageVariant.id);
    }

    await testing.withRealCart(variantIdsToRestore, async function() {
        if(pageVariant !== undefined) {
            await testing.addVariant(pageVariant.id);
        }

        let success = await product.protectCheck();
        assert(success === true, 'Expected protectCheck to complete successfully against the live cart.');
    });
});

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', "adding the current page's product then running protectCheck resolves Protect correctly", async function() {
    let assert = SWAP.Protect.Testing.assert;
    let product = SWAP.Protect.Product;

    let pageProduct = await SWAP.Protect.Testing.getCurrentPageProduct();

    assert(pageProduct !== undefined, 'No product could be found on the current page - run this test from a product page.');

    let variant = pageProduct.variants.find(function(v) { return v.available; }) || pageProduct.variants[0];

    assert(variant !== undefined, "The current page's product has no variants.");

    let protectVariantIds = product.Protect.variants.map(function(v) { return v.id; });

    await SWAP.Protect.Testing.withRealCart([variant.id, ...protectVariantIds], async function() {
        await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
        });

        let cart = await product.cartGet();
        let checked = await product.protectCheck(cart);

        assert(checked === true, 'Expected protectCheck to complete successfully.');

        let recheckCart = await product.cartGet();
        let action = await product.protectReview(recheckCart);

        assert(action === product.ReviewAction.NONE, 'Expected the cart to need no further changes after protectCheck, got ' + action + '.');
    });
});

// The following tests exercise every ReviewAction protectReview can return, end-to-end
// through protectCheck, against a deliberately-constructed live cart (emptied and rebuilt via
// withEmptyCart, which fully restores whatever was really in the cart afterwards). They all
// need a real product to work with, so - like the test above - they must be run from a
// product page. The "over limit" REMOVE case is deliberately not covered here, since
// reproducing it would mean actually adding a huge amount of real product value to the cart.

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'protectReview suggests REMOVE and protectCheck clears Protect when the current product becomes restricted', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let testing = SWAP.Protect.Testing;
    let product = SWAP.Protect.Product;

    let pageProduct = await testing.getCurrentPageProduct();
    assert(pageProduct !== undefined, 'No product could be found on the current page - run this test from a product page.');

    let variant = pageProduct.variants.find(function(v) { return v.available; }) || pageProduct.variants[0];
    assert(variant !== undefined, "The current page's product has no variants.");

    await testing.withEmptyCart(async function() {
        await testing.withRestrictionsOverride({ products: [pageProduct.id] }, async function() {
            await testing.addVariant(variant.id);

            let cart = await product.cartGet();
            let action = await product.protectReview(cart);

            assert(action === product.ReviewAction.REMOVE, 'Expected REMOVE for a restricted product, got ' + action + '.');

            let checked = await product.protectCheck(cart);
            assert(checked === true, 'Expected protectCheck to complete successfully.');

            let recheckCart = await product.cartGet();
            let fee = product.protectCalculateFee(recheckCart);

            assert(fee.feeTotalCurrent === 0, 'Expected no Protect fee to remain for a restricted product, got ' + fee.feeTotalCurrent + '.');
        });
    });
});

SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'protectReview suggests REMOVE and protectCheck clears an orphaned Protect fee', async function() {
    let assert = SWAP.Protect.Testing.assert;
    let testing = SWAP.Protect.Testing;
    let product = SWAP.Protect.Product;

    let protectVariant = product.Protect.variants[0];
    assert(protectVariant !== undefined, 'The live Protect product has no variants.');

    await testing.withEmptyCart(async function() {
        await testing.addVariant(protectVariant.id);

        let cart = await product.cartGet();
        let action = await product.protectReview(cart);

        assert(action === product.ReviewAction.REMOVE, 'Expected REMOVE for an orphaned Protect fee, got ' + action + '.');

        let checked = await product.protectCheck(cart);
        assert(checked === true, 'Expected protectCheck to complete successfully.');

        let recheckCart = await product.cartGet();
        let fee = product.protectCalculateFee(recheckCart);

        assert(fee.feeTotalCurrent === 0, 'Expected the orphaned Protect fee to be removed, got ' + fee.feeTotalCurrent + '.');
    });
});

[
    { key: 'under', label: 'a cheaper bracket than required is applied', expectedAction: 'UPDATE' },
    { key: 'over', label: 'a pricier bracket than required is applied', expectedAction: 'UPDATE' },
    { key: 'correct', label: 'the correct bracket is already applied', expectedAction: 'NONE' }
].forEach(function(scenario) {
    SWAP.Protect.Testing.register('Live: SWAP.Protect.Product', 'protectReview suggests ' + scenario.expectedAction + ' and protectCheck resolves it when ' + scenario.label, async function() {
        let assert = SWAP.Protect.Testing.assert;
        let testing = SWAP.Protect.Testing;
        let product = SWAP.Protect.Product;

        let pageProduct = await testing.getCurrentPageProduct();
        assert(pageProduct !== undefined, 'No product could be found on the current page - run this test from a product page.');

        let variant = pageProduct.variants.find(function(v) { return v.available; }) || pageProduct.variants[0];
        assert(variant !== undefined, "The current page's product has no variants.");

        await testing.withEmptyCart(async function() {
            await testing.addVariant(variant.id);

            let cart = await product.cartGet();
            let brackets = testing.getProtectBrackets(cart);
            let applied = brackets[scenario.key];

            assert(applied !== undefined, 'There is no "' + scenario.key + '" bracket available to test with on this store/product.');
            assert(brackets.correct !== undefined, 'Could not determine the correct Protect bracket for this cart.');

            await testing.addVariant(applied.id);

            let preCart = await product.cartGet();
            let action = await product.protectReview(preCart);

            assert(action === product.ReviewAction[scenario.expectedAction], 'Expected ' + scenario.expectedAction + ', got ' + action + '.');

            let checked = await product.protectCheck(preCart);
            assert(checked === true, 'Expected protectCheck to complete successfully.');

            let recheckCart = await product.cartGet();
            let recheckAction = await product.protectReview(recheckCart);

            assert(recheckAction === product.ReviewAction.NONE, 'Expected the cart to end at NONE after protectCheck, got ' + recheckAction + '.');

            let fee = product.protectCalculateFee(recheckCart);
            assert(fee.id === brackets.correct.id, 'Expected the correct bracket variant ' + brackets.correct.id + ' to be applied, got ' + fee.id + '.');
        });
    });
});

// #endregion >> Group: Live: SWAP.Protect.Product

// #endregion
